/**
 * The arithmetic every line technique on /nonogram/techniques is built from.
 *
 * A line's runs can only slide so far. Pack them all as far left as they go, pack
 * them all as far right, and any square a run covers in both packings is a square it
 * covers in every legal arrangement in between — a run is unbroken, so it cannot skip
 * a square it spans at both ends of its travel. That is the overlap technique, and it
 * is also what the teaching pages draw.
 *
 * The solver in solver.ts answers "how many solutions does this grid have"; this
 * answers "what does one line force on its own", which is the question a player is
 * actually asking. Keeping them apart means the teaching pages cannot accidentally
 * show a deduction the solver reached by branching, which no reader could follow.
 * lineFacts does borrow the solver's placement enumerator, but only that: enumerating
 * one line's placements is line reasoning, and it is what a player does by hand.
 */

import type { CellMark } from "./types";
import { generateLineMasks } from "./solver";

export interface LineRun {
  /** The clue's length, in the order it appears in the line. */
  clue: number;
  /** First square this run occupies when every run is packed as far left as it goes. */
  earliest: number;
  /** First square this run occupies when every run is packed as far right as it goes. */
  latest: number;
}

/** The shortest line these clues fit in: the runs themselves plus one gap between
    each neighbouring pair. */
export function lineMinimumLength(clues: readonly number[]): number {
  if (clues.length === 0) return 0;
  return clues.reduce((total, clue) => total + clue, 0) + clues.length - 1;
}

/**
 * How far the whole arrangement can slide. Negative means the clues do not fit, which
 * is a broken puzzle rather than a hard one — callers get the number rather than an
 * exception so a demo can say so instead of crashing the page.
 */
export function lineSlack(length: number, clues: readonly number[]): number {
  return length - lineMinimumLength(clues);
}

/** Where each run sits in the two extreme packings. Empty when the clues do not fit. */
export function lineRuns(length: number, clues: readonly number[]): LineRun[] {
  const slack = lineSlack(length, clues);
  if (slack < 0) return [];

  const runs: LineRun[] = [];
  let earliest = 0;
  for (const clue of clues) {
    runs.push({ clue, earliest, latest: earliest + slack });
    earliest += clue + 1;
  }
  return runs;
}

/**
 * The squares one run covers in every arrangement, or undefined when it covers none.
 * A run of length c on a line with slack s overlaps c − s squares; at c ≤ s the two
 * packings do not touch and the clue says nothing on its own yet.
 */
export function runOverlap(run: LineRun): { start: number; length: number } | undefined {
  const length = run.earliest + run.clue - run.latest;
  return length > 0 ? { start: run.latest, length } : undefined;
}

/** One flag per square: true where the line is filled in every legal arrangement. */
export function overlapCells(length: number, clues: readonly number[]): boolean[] {
  const cells = Array.from({ length }, () => false);
  for (const run of lineRuns(length, clues)) {
    const overlap = runOverlap(run);
    if (!overlap) continue;
    for (let index = overlap.start; index < overlap.start + overlap.length; index += 1) cells[index] = true;
  }
  return cells;
}

/** One flag per square for a single packing — what the demo draws for "hard left" and
    "hard right" before it draws what the two have in common. */
export function packedCells(length: number, runs: readonly LineRun[], edge: "earliest" | "latest"): boolean[] {
  const cells = Array.from({ length }, () => false);
  for (const run of runs) {
    const start = run[edge];
    for (let index = start; index < start + run.clue && index < length; index += 1) cells[index] = true;
  }
  return cells;
}

/**
 * What a line forces once some of its squares are already known.
 *
 * Overlap is this with nothing known yet; edge logic and forcing are this with one
 * square settled by a crossing line. Writing them as one function rather than three
 * is not tidiness — it is the actual claim the technique pages make, that these are
 * three ways of noticing the same deduction rather than three different rules.
 *
 * `known` uses the board's own marks (1 filled, -1 crossed, 0 not yet known), which
 * is deliberately *not* the encoding solver.ts uses internally for the same three
 * states. The board's is the one a page config is written in.
 */
export interface LineFacts {
  /** Placements still legal given `known`. Zero means the marks contradict the clues. */
  placements: number;
  /** The leftmost and rightmost surviving placements, one flag per square. */
  earliest: boolean[];
  latest: boolean[];
  /** Filled in every surviving placement — squares you may fill in. */
  filled: boolean[];
  /** Empty in every surviving placement — squares you may cross out. */
  empty: boolean[];
}

function marksAllow(mask: number, known: readonly CellMark[]): boolean {
  for (let index = 0; index < known.length; index += 1) {
    const mark = known[index];
    if (mark === 0 || mark === undefined) continue;
    const filled = (mask & (1 << index)) !== 0;
    if (mark === 1 && !filled) return false;
    if (mark === -1 && filled) return false;
  }
  return true;
}

function maskToCells(mask: number, length: number): boolean[] {
  return Array.from({ length }, (_, index) => (mask & (1 << index)) !== 0);
}

export function lineFacts(length: number, clues: readonly number[], known: readonly CellMark[] = []): LineFacts {
  const blank = Array.from({ length }, () => false);
  // generateLineMasks walks the runs left to right, so the surviving placements come
  // back in that order: the first is the leftmost that still fits and the last is the
  // rightmost. That is what the demo draws for its two middle steps.
  const survivors = generateLineMasks(length, clues).filter((mask) => marksAllow(mask, known));
  if (survivors.length === 0) {
    return { placements: 0, earliest: blank, latest: [...blank], filled: [...blank], empty: [...blank] };
  }

  const filled = Array.from({ length }, (_, index) => survivors.every((mask) => (mask & (1 << index)) !== 0));
  const empty = Array.from({ length }, (_, index) => survivors.every((mask) => (mask & (1 << index)) === 0));

  return {
    placements: survivors.length,
    earliest: maskToCells(survivors[0]!, length),
    latest: maskToCells(survivors.at(-1)!, length),
    filled,
    empty,
  };
}
